import { Component, computed, effect, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faAnglesLeft } from '@fortawesome/free-solid-svg-icons';
import { Question, Questionnaire } from '../models';
import { ApiService } from '../services/api.service';

@Component({
    selector: 'fgai-history-answer',
    imports: [CommonModule, FormsModule, FontAwesomeModule],
    templateUrl: './history-answer.html',
    styleUrl: './history-answer.scss',
})
export class HistoryAnswer {
    faAnglesLeft = faAnglesLeft;

    /**
     * Current questionnaire
     */
    questionnaire = input<Questionnaire>();
    /**
     * Current question being edited
     */
    question = input<Question>();
    /**
     * The user wants to copy the content of this question
     */
    copyQuestion = output<Question>();

    /**
     * Questionnaires for this project, which aren't the current questionnaire
     */
    projectQuestionnaires = signal<Questionnaire[]>([]);
    /**
     * Which of the older questionnaires did the user choose?
     */
    selectedQuestionnaire = signal('');
    /**
     * The question of the selected questionnaire which matches the current question
     */
    selectedQuestion = signal<Question | undefined>(undefined);

    dateFormat = computed(() => {
        const questionnaires = this.projectQuestionnaires();
        const dateStrings = new Set<string>();
        for (const questionnaire of questionnaires) {
            const date = questionnaire.date_edited.toDateString()
            if (dateStrings.has(date)) {
                // multiple have the same date, we also need to add the time
                return 'medium';
            }
            dateStrings.add(date);
        }

        return 'mediumDate';
    });

    private questionnaireCache: { [id: string]: Promise<Questionnaire> } = {};

    constructor(private apiService: ApiService) {
        effect(async () => {
            const questionnaire = this.questionnaire();
            if (!questionnaire) {
                return;
            }

            const project = await apiService.projects.get(questionnaire.project);
            // get the questionnaires for the project
            const questionnaires = (await Promise.all(
                project.questionnaire
                    .filter(qid => qid !== questionnaire.id)
                    .map(qid => apiService.questionnaires.get(qid))
            )).sort((a, b) =>
                a.date_edited < b.date_edited
                    ? -1
                    : a.date_edited == b.date_edited
                        ? 0 : 1);

            if (questionnaires.length) {
                this.selectedQuestionnaire.set(questionnaires[0].id);
                this.projectQuestionnaires.set(questionnaires);
            }
        });

        effect(async () => {
            const selectedQuestionnaire = this.selectedQuestionnaire();
            const currentQuestion = this.question();
            if (selectedQuestionnaire && currentQuestion) {
                // determine the selected question
                const questionnaire = await this.getQuestionnaire(selectedQuestionnaire);
                for (const question of questionnaire.questions) {
                    if (question.sequence_number === currentQuestion.sequence_number) {
                        this.selectedQuestion.set(question);
                        return;
                    }
                }
            }
        });
    }

    /**
     * Triggers an emit of the currently selected question
     */
    emitCopyQuestion() {
        const candidate = this.selectedQuestion();
        if (candidate) {
            this.copyQuestion.emit(candidate);
        }
    }

    private async getQuestionnaire(id: string) {
        let questionnaire = this.questionnaireCache[id];
        if (questionnaire) {
            return questionnaire;
        }

        questionnaire = this.apiService.questionnaires.get(id);
        this.questionnaireCache[id] = questionnaire;
        return questionnaire;
    }
}
